import { useState, type ReactElement } from "react";
import { Link as RouterLink } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import Box from "@mui/material/Box";
import Skeleton from "@mui/material/Skeleton";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { alpha } from "@mui/material/styles";
import WifiIcon from "@mui/icons-material/WifiOutlined";
import HistoryIcon from "@mui/icons-material/HistoryOutlined";
import DataUsageIcon from "@mui/icons-material/DataUsageOutlined";
import ScheduleIcon from "@mui/icons-material/ScheduleOutlined";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmptyOutlined";
import LockIcon from "@mui/icons-material/LockOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircleOutline";
import TimerIcon from "@mui/icons-material/TimerOutlined";
import EventBusyIcon from "@mui/icons-material/EventBusyOutlined";
import PersonAddIcon from "@mui/icons-material/PersonAddOutlined";
import AutorenewIcon from "@mui/icons-material/AutorenewOutlined";
import GroupsIcon from "@mui/icons-material/GroupsOutlined";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLongOutlined";
import PersonSearchIcon from "@mui/icons-material/PersonSearchOutlined";
import PaymentsIcon from "@mui/icons-material/PaymentsOutlined";
import WalletIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import CreditCardOffIcon from "@mui/icons-material/CreditCardOffOutlined";
import LocalOfferIcon from "@mui/icons-material/LocalOfferOutlined";
import SmsIcon from "@mui/icons-material/SmsOutlined";
import BadgeIcon from "@mui/icons-material/BadgeOutlined";
import ApartmentIcon from "@mui/icons-material/ApartmentOutlined";
import BarChartIcon from "@mui/icons-material/BarChartOutlined";
import { useIdentity } from "../../auth/useIdentity";
import { fetchReport } from "../../api/reports";
import type { NavLeaf } from "../../components/nav";
import { hintFor } from "../../components/navSearch";
import { useNavGroups } from "../../components/useNavGroups";
import { REPORT_SPECS } from "./catalog";
import { REPORT_FACTS } from "./reportFacts";

/** The reports whose title or guidance holds every word typed. */
export function filterReports(reports: NavLeaf[], query: string): NavLeaf[] {
  const q = query.toLowerCase().split(/\s+/).filter(Boolean);
  return reports.filter((r) => {
    const text = `${r.label} ${hintFor(r.to) ?? ""}`.toLowerCase();
    return q.every((w) => text.includes(w));
  });
}

type Tone = "primary" | "secondary" | "success" | "warning" | "info" | "error";

/** Each report's icon and colour (owner 2026-10-25: "add icon to each … more colourful"). */
export const LOOK: Record<string, { icon: ReactElement; tone: Tone }> = {
  "active-sessions": { icon: <WifiIcon />, tone: "success" },
  "user-sessions": { icon: <HistoryIcon />, tone: "info" },
  "top-usage": { icon: <DataUsageIcon />, tone: "primary" },
  "peak-hours": { icon: <ScheduleIcon />, tone: "secondary" },
  "never-used": { icon: <HourglassEmptyIcon />, tone: "warning" },
  "failed-logins": { icon: <LockIcon />, tone: "error" },
  "active-plans": { icon: <CheckCircleIcon />, tone: "success" },
  "expiring-plans": { icon: <TimerIcon />, tone: "warning" },
  "expired-not-renewed": { icon: <EventBusyIcon />, tone: "error" },
  "new-subscribers": { icon: <PersonAddIcon />, tone: "primary" },
  "renewals-by-plan": { icon: <AutorenewIcon />, tone: "info" },
  "base-snapshot": { icon: <GroupsIcon />, tone: "secondary" },
  "recharge-log": { icon: <ReceiptLongIcon />, tone: "primary" },
  "recharge-log-by-user": { icon: <PersonSearchIcon />, tone: "info" },
  collections: { icon: <PaymentsIcon />, tone: "success" },
  "funds-statement": { icon: <WalletIcon />, tone: "secondary" },
  "payment-failures": { icon: <CreditCardOffIcon />, tone: "error" },
  "promo-use": { icon: <LocalOfferIcon />, tone: "warning" },
  "sms-log": { icon: <SmsIcon />, tone: "info" },
  "staff-activity": { icon: <BadgeIcon />, tone: "primary" },
  "tenant-summary": { icon: <ApartmentIcon />, tone: "secondary" },
};

const slugOf = (to: string) => to.slice("/reports/".length);

/** The card's live line: the report asked once, its answer read down to one figure. */
function FactLine({ slug }: { slug: string }) {
  const identity = useIdentity();
  const fact = REPORT_FACTS[slug];
  const endpoint = REPORT_SPECS.find((r) => r.slug === slug)?.endpoint;
  const [today] = useState(() => new Date());
  const q = useQuery({
    queryKey: ["report-fact", slug, identity.namespace],
    enabled: Boolean(fact && endpoint && identity.token && identity.namespace),
    staleTime: 5 * 60_000,
    retry: false,
    queryFn: ({ signal }) => fetchReport(identity, endpoint!, fact!.filter(today), signal),
  });
  if (!fact) return null;
  if (q.isLoading) return <Skeleton variant="text" width="70%" />;
  // A fact that cannot be had is left out; the card still opens the report.
  const line = q.data ? fact.line(q.data.data) : null;
  if (!line) return null;
  return (
    <Typography variant="body2" sx={{ fontWeight: 600 }} data-testid={`fact-${slug}`}>
      {line}
    </Typography>
  );
}

function ReportCard({ report }: { report: NavLeaf }) {
  const slug = slugOf(report.to);
  const look = LOOK[slug] ?? { icon: <BarChartIcon />, tone: "primary" as Tone };
  const hint = hintFor(report.to);
  return (
    <Box
      component={RouterLink}
      to={report.to}
      sx={{
        display: "flex",
        flexDirection: "column",
        border: 1,
        borderColor: "divider",
        borderRadius: "8px",
        overflow: "hidden",
        textDecoration: "none",
        color: "inherit",
        bgcolor: "background.paper",
        transition: "border-color 120ms, box-shadow 120ms",
        "&:hover": { borderColor: `${look.tone}.main`, boxShadow: 2 },
        "&:focus-visible": { outline: 2, outlineColor: `${look.tone}.main`, outlineOffset: 2 },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.25,
          px: 2,
          py: 1.25,
          bgcolor: (t) => alpha(t.palette[look.tone].main, 0.1),
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Box
          sx={{
            display: "grid",
            placeItems: "center",
            width: 34,
            height: 34,
            borderRadius: "8px",
            color: "common.white",
            bgcolor: `${look.tone}.main`,
            flexShrink: 0,
            "& svg": { fontSize: 20 },
          }}
        >
          {look.icon}
        </Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
          {report.label}
        </Typography>
      </Box>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, px: 2, py: 1.5, flex: 1 }}>
        <FactLine slug={slug} />
        {hint && (
          <Typography variant="caption" color="text.secondary">
            {hint}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

/**
 * A Reports category (owner 2026-10-25): the sidebar shows four categories and
 * each opens this page, a card per report: its icon and name on top, one line
 * of live information and what it is for underneath, the whole card a link.
 * The cards come from the sidebar's own groups, so a report the sidebar hides
 * from this login is not offered here either.
 */
export function ReportCategoryPage({ path }: { path: string }) {
  const groups = useNavGroups();
  const [query, setQuery] = useState("");
  const category = groups.find((g) => g.key === "reports")?.items.find((i) => i.to === path);
  const reports = category?.sub ?? [];
  const shown = filterReports(reports, query);
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
        <Typography variant="h5" component="h1" sx={{ flex: 1 }}>
          {category?.label ?? "Reports"}
        </Typography>
        {reports.length > 3 && (
          <TextField
            size="small"
            placeholder="Filter reports"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            slotProps={{ htmlInput: { "aria-label": "Filter reports" } }}
          />
        )}
      </Box>
      {reports.length === 0 && <Typography color="text.secondary">No reports here for this login.</Typography>}
      {reports.length > 0 && shown.length === 0 && (
        <Typography color="text.secondary">No report matches “{query}”.</Typography>
      )}
      <Box sx={{ display: "grid", gap: 1.5, gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))" }}>
        {shown.map((r) => (
          <ReportCard key={r.to} report={r} />
        ))}
      </Box>
    </Box>
  );
}
